import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue";
import _imports_0 from '@/assets/svgs/Chat.svg';
const _hoisted_1 = { class: "sms-table" };
const _hoisted_2 = /*#__PURE__*/ _createElementVNode("hr", null, null, -1);
const _hoisted_3 = { key: 0 };
const _hoisted_4 = { key: 1 };
const _hoisted_5 = ["innerHTML"];
const _hoisted_6 = /*#__PURE__*/ _createElementVNode("img", {
    src: _imports_0,
    class: "sms-table__message-icon"
}, null, -1);
const _hoisted_7 = { key: 2 };
const _hoisted_8 = /*#__PURE__*/ _createElementVNode("span", { class: "sms-table__dropdown__button" }, [
    /*#__PURE__*/ _createElementVNode("i", { class: "icon icon-menu-14px" })
], -1);
const _hoisted_9 = { class: "el-table__column" };
const _hoisted_10 = /*#__PURE__*/ _createElementVNode("i", { class: "icon icon-question-12px" }, null, -1);
const _hoisted_11 = {
    key: 0,
    class: "sms-table__overlay"
};
import { computed, reactive, ref, onMounted } from 'vue';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
import { mixpanelSmsCampaign } from '@/utilities/mixpanel/smsCampaigns';
import { STATUS } from '@/features/SMSCampaigns/utils';
import i18n from '@/i18n';
import store from '@/store';
import InfoCard from '@/components/_atoms/InfoCard.vue';
import ListPagination from '@/components/_atoms/ListPagination.vue';
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner.vue';
import ModalInactivateConfirm from '@/features/SMSCampaigns/components/list/SMSCampaignsModalInactivate.vue';
import moment from 'moment';
import { useToast } from 'vue-toastification';
import { fetchSmsCampaignList } from '@/features/SMSCampaigns/data';
import { useSmsBalance } from '@/features/SMSCampaigns/composables/useSmsBalance';
const SMS_CAMPAIGNS_QUERY_KEY = 'sms-campaigns-table';
const STALE_TIME = 1000 * 60 * 60;
export default /*#__PURE__*/ _defineComponent({
    __name: 'SMSCampaignsTable',
    setup(__props) {
        const { updateBalance } = useSmsBalance();
        const toast = useToast();
        const { t } = i18n.global;
        const queryClient = useQueryClient();
        const campaignToInactivate = ref(null);
        const pagination = reactive({
            limit: 10,
            page: 1,
        });
        const totalItems = computed(() => campaigns.value?.totalCount);
        const hasError = computed(() => !loading.value && !campaigns.value?.success);
        const isEmpty = computed(() => !loading.value && campaigns.value?.success && !campaigns.value.data.length);
        const queryKey = computed(() => [
            SMS_CAMPAIGNS_QUERY_KEY,
            {
                page: pagination.page,
                limit: pagination.limit,
                brandId: store.getters.selectedBrand.id,
            },
        ]);
        const { data: campaigns, isPending: loading, isFetching, } = useQuery({
            queryKey,
            queryFn: () => fetchSmsCampaignList(pagination),
            staleTime: STALE_TIME,
            placeholderData: keepPreviousData,
        });
        const reloadCampaigns = () => {
            queryClient.invalidateQueries({ queryKey: [SMS_CAMPAIGNS_QUERY_KEY] });
        };
        const getHeaderTooltip = (columnKey) => {
            const tooltipByColumn = {
                sent: 'sms-campaigns.table.sent-tooltip',
                total: 'sms-campaigns.table.people-tooltip',
            };
            return tooltipByColumn[columnKey];
        };
        const getStatusLabel = (status) => {
            const statusLabels = {
                [STATUS.COMPLETED]: i18n.global.t('sms-campaigns.status.completed'),
                [STATUS.IN_PROGRESS]: i18n.global.t('sms-campaigns.status.in-progress'),
                [STATUS.SCHEDULED]: i18n.global.t('sms-campaigns.status.scheduled'),
                [STATUS.DISABLED]: i18n.global.t('sms-campaigns.status.inactive'),
            };
            return statusLabels[status] ?? status;
        };
        const getStatusDotClass = (status) => `sms-table__status-dot--${status}`;
        const getColumnName = (key) => {
            return campaigns.value.columns.filter(column => column.key === key)[0]['label'];
        };
        const canInactivateCampaign = (campaign) => {
            if (!campaign.createDate)
                return true;
            const now = moment();
            const minInactivateDate = moment(campaign.createDate, 'YYYY-MM-DD HH:mm:ss').add(30, 'minutes');
            return now.isSameOrAfter(minInactivateDate);
        };
        const openInactivateCampaignConfirmation = (campaign) => {
            if (!canInactivateCampaign(campaign)) {
                const formattedHour = moment(campaign.createDate, 'YYYY-MM-DD HH:mm:ss').add(30, 'minutes').format('HH:mm');
                mixpanelSmsCampaign.errorInactiveCampaign();
                toast.error(t('sms-campaigns.table.toast-cannot-inactivate-before-create-date', { hour: formattedHour }), {
                    timeout: 5000,
                });
                return;
            }
            mixpanelSmsCampaign.inactivateClick(campaign);
            campaignToInactivate.value = campaign;
        };
        const cannotInactivateCampaign = (status) => {
            const STATUSES_THAT_CANNOT_BE_INACTIVATED = [STATUS.COMPLETED, STATUS.IN_PROGRESS, STATUS.DISABLED];
            return STATUSES_THAT_CANNOT_BE_INACTIVATED.includes(status);
        };
        const cancelInactivateCampaign = () => {
            campaignToInactivate.value = null;
        };
        const getActionLabel = (status) => {
            const showInactivateLabel = [STATUS.SCHEDULED, STATUS.COMPLETED, STATUS.IN_PROGRESS];
            const label = showInactivateLabel.includes(status) ? '_inactivate' : '_activate';
            return i18n.global.t(label, { count: 2 });
        };
        const getColumnWidth = (columnKey) => {
            const defaultWidth = 90;
            const widths = {
                status: 80,
                name: 220,
                message: 100,
                sentDate: 100,
                total: 110,
                delivered: 90,
                actions: 50,
            };
            return widths[columnKey] || defaultWidth;
        };
        const getCellClass = ({ column }) => {
            if (['sent', 'total', 'actions'].includes(column.property)) {
                return 'sms-table__cell-right';
            }
            return '';
        };
        const onPageChange = (_reset, data) => {
            pagination.page = data.page;
            pagination.limit = data.limit;
        };
        const reloadTable = () => {
            reloadCampaigns();
            updateBalance();
        };
        onMounted(() => {
            reloadCampaigns();
        });
        return (_ctx, _cache) => {
            const _component_el_popover = _resolveComponent("el-popover");
            const _component_el_dropdown_item = _resolveComponent("el-dropdown-item");
            const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu");
            const _component_el_dropdown = _resolveComponent("el-dropdown");
            const _component_el_table_column = _resolveComponent("el-table-column");
            const _component_el_table = _resolveComponent("el-table");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _hoisted_2,
                _createVNode(_component_el_table, {
                    data: _unref(campaigns)?.data,
                    "header-cell-class-name": getCellClass,
                    "cell-class-name": getCellClass,
                    class: _normalizeClass(['sms-table__content', { 'sms-table__content--loading': _unref(isFetching) }])
                }, {
                    empty: _withCtx(() => [
                        (_unref(isFetching))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createVNode(BaseLoader)
                            ]))
                            : _createCommentVNode("", true),
                        (hasError.value)
                            ? (_openBlock(), _createBlock(InfoCard, {
                                key: 1,
                                ref: "message-error",
                                error: ""
                            }, null, 512))
                            : (isEmpty.value)
                                ? (_openBlock(), _createBlock(InfoCard, {
                                    key: 2,
                                    "custom-title": _ctx.$t('sms-campaigns.table.empty.title'),
                                    "custom-message": _ctx.$t('sms-campaigns.table.empty.message'),
                                    ref: "message-error"
                                }, null, 8, ["custom-title", "custom-message"]))
                                : _createCommentVNode("", true)
                    ]),
                    default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(campaigns)?.columns, (column, index) => {
                            return (_openBlock(), _createBlock(_component_el_table_column, {
                                key: index,
                                prop: column.key,
                                label: column.label,
                                "min-width": getColumnWidth(column.key)
                            }, {
                                default: _withCtx((scope) => [
                                    (column.key === 'status')
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                            _createElementVNode("span", {
                                                class: _normalizeClass(["sms-table__status-dot", getStatusDotClass(scope.row.status)])
                                            }, null, 2),
                                            _createTextVNode(" " + _toDisplayString(getStatusLabel(scope.row.status)), 1)
                                        ]))
                                        : _createCommentVNode("", true),
                                    (column.key === 'message')
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                            _createVNode(_component_el_popover, {
                                                effect: "dark",
                                                trigger: "hover",
                                                placement: "top",
                                                width: 300
                                            }, {
                                                reference: _withCtx(() => [
                                                    _hoisted_6
                                                ]),
                                                default: _withCtx(() => [
                                                    _createElementVNode("span", {
                                                        innerHTML: scope.row.message
                                                    }, null, 8, _hoisted_5)
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ]))
                                        : _createCommentVNode("", true),
                                    (column.key === 'actions')
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                            _createVNode(_component_el_dropdown, {
                                                trigger: "click",
                                                class: "sms-table__dropdown"
                                            }, {
                                                dropdown: _withCtx(() => [
                                                    _createVNode(_component_el_dropdown_menu, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_el_dropdown_item, {
                                                                onClick: ($event) => (openInactivateCampaignConfirmation(scope.row)),
                                                                disabled: cannotInactivateCampaign(scope.row.status)
                                                            }, {
                                                                default: _withCtx(() => [
                                                                    _createTextVNode(_toDisplayString(getActionLabel(scope.row.status)), 1)
                                                                ]),
                                                                _: 2
                                                            }, 1032, ["onClick", "disabled"])
                                                        ]),
                                                        _: 2
                                                    }, 1024)
                                                ]),
                                                default: _withCtx(() => [
                                                    _hoisted_8
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ]))
                                        : _createCommentVNode("", true)
                                ]),
                                header: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_9, [
                                        _createElementVNode("label", null, _toDisplayString(getColumnName(column.key)), 1),
                                        (getHeaderTooltip(column.key))
                                            ? (_openBlock(), _createBlock(_component_el_popover, {
                                                key: 0,
                                                trigger: "hover",
                                                placement: "top",
                                                effect: "dark",
                                                width: 350
                                            }, {
                                                reference: _withCtx(() => [
                                                    _hoisted_10
                                                ]),
                                                default: _withCtx(() => [
                                                    _createElementVNode("p", null, _toDisplayString(_ctx.$t(getHeaderTooltip(column.key))), 1)
                                                ]),
                                                _: 2
                                            }, 1024))
                                            : _createCommentVNode("", true)
                                    ])
                                ]),
                                _: 2
                            }, 1032, ["prop", "label", "min-width"]));
                        }), 128))
                    ]),
                    _: 1
                }, 8, ["data", "class"]),
                _createVNode(ListPagination, {
                    onChangePage: onPageChange,
                    data: { ...pagination, count: totalItems.value },
                    limit: pagination.limit,
                    "limit-range": [5, 10, 20, 30, 50, 100],
                    id: "sms-campaigns-table"
                }, null, 8, ["data", "limit"]),
                (campaignToInactivate.value)
                    ? (_openBlock(), _createBlock(ModalInactivateConfirm, {
                        key: 0,
                        onCloseModal: cancelInactivateCampaign,
                        onReloadTable: reloadTable,
                        "is-visible": true,
                        campaign: campaignToInactivate.value
                    }, null, 8, ["campaign"]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
